// `post.page.tpl.html` — the tab an OAuth provider redirects back to after the user connects
// their mailbox. It exists to tell the extension the connect finished and to say so to the
// human, who has a stray browser tab open.
//
// ⚠️ THE ONE-SECOND DELAY IS THE SCREEN. `$timeout(…, 1000)` flips `loading` and only then is
// there a message — so the spinner is not a loading state waiting on anything, it is a beat.
// The reference capture is of the settled state (56 characters, the default message), which is
// what the capture's readiness wait reaches; rendering the message immediately would be a
// different screen for the first second of every visit.
import { useEffect, useState, type ReactElement } from 'react'
import { PageHost } from '../auth/PageHost.tsx'
import { asset } from '../assetBase.ts'

/** `BroadcastChannelEventType` — what the provider's callback puts in `?type`. */
export type PostEventType =
  | 'gmailConnected'
  | 'gmailConnectError'
  | 'outlookConnected'
  | 'outlookConnectError'
  | 'exchangeConnected'

/**
 * `ExtensionEmailIntegrationPhase.EMAIL_SIGNUP` — the phase that changes the success copy.
 *
 * ⚠️ THE ENUM'S VALUE IS ITS NAME, `'EMAIL_SIGNUP'`, and that is what careero puts in `?phase=`.
 * This was `'emailSignup'`, a guess that never matched a real callback: after a real Gmail connect
 * (28 Oct) the page said "Gmail is now connected…" where upstream says "You're all set!".
 */
export const EMAIL_SIGNUP_PHASE = 'EMAIL_SIGNUP'

/**
 * The sentence for a `?type`, exactly as the controller's switch builds it.
 *
 * ⚠️ THE DEFAULT IS NOT AN ERROR. Anything unrecognised — including no type at all, which is
 * every reference capture — gets "Done. You may now close this page and return to your ATS".
 * Two of the five types have no case of their own and land there too: `exchangeConnected` is
 * declared in the enum and never matched, so an Exchange connect says "Done." rather than
 * naming Exchange.
 */
export function postMessageText(type: string | undefined, phase: string | undefined): string {
  const connected = (client: string): string =>
    phase === EMAIL_SIGNUP_PHASE
      ? "You're all set! You may now return to your ATS and start using ‘Woo’s Smart Search’"
      : `${client} is now connected. You may now close this page and return to your ATS`

  switch (type) {
    case 'gmailConnected':
      return connected('Gmail')
    case 'gmailConnectError':
      return 'Error connecting to Gmail. You may now close this page, return to your ATS and try again'
    case 'outlookConnected':
      return connected('Outlook')
    case 'outlookConnectError':
      return 'Error connecting to Outlook. You may now close this page, return to your ATS and try again'
    default:
      return 'Done. You may now close this page and return to your ATS'
  }
}

export interface PostPageProps {
  /** `$stateParams.type`. */
  type?: string
  /** `$stateParams.phase`. */
  phase?: string
  /**
   * Announces the connect to whatever is listening — the point of the screen.
   * `broadcastChannelService.create(PostChannel).postMessage({type})` is what tells the panel that
   * started the connect that the mailbox is connected. The SPA passes `publishPostEvent`
   * (`apps/spa/src/postChannel.ts`), which goes through `BroadcastChannelService` so it reaches
   * the ATS tab's iframe by the extension route as well as same-origin tabs natively.
   */
  onConnected?: (type: string | undefined) => void
  /** Milliseconds before the message replaces the spinner. Upstream's is 1000. */
  delayMs?: number
}

export function PostPage({ type, phase, onConnected, delayMs = 1000 }: PostPageProps): ReactElement {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    onConnected?.(type)
    const timer = setTimeout(() => {
      setLoading(false)
    }, delayMs)
    return () => {
      clearTimeout(timer)
    }
  }, [type, onConnected, delayMs])

  return (
    <PageHost name="chrome-extension-post-page">
      <img className="logo" src={asset('/static/assets/img/woo/logo.svg')} alt="" />
      {loading ? (
        <div className="text-center">
          <div className="title">Redirecting...</div>
          <i className="far fa-spinner fa-spin woo-loader" />
        </div>
      ) : (
        <div className="message">{postMessageText(type, phase)}</div>
      )}
    </PageHost>
  )
}
